import React, {useEffect, useState, useContext} from "react";
import {useNavigate, useLocation} from 'react-router-dom';
import ConfigContext from "../../contexts/ConfigContext";
import {
    addNewOffer,
    deleteOffer,
    fetchOffers,
    getToken,
    getUserData,
    isTokenValid,
    updateOffer
} from "../../api/api";
import styles from './LookingPartner.module.css'
import Spinner from "../Spinner/Spinner";
import ErrorMessage from "../ErrorMessage/ErrorMessage";
import {FaEnvelope, FaFacebookMessenger, FaLock, FaPhone, FaWhatsapp} from "react-icons/fa";
import CustomModal from "../../UI/Modal/CustomModal";
import NtrpRangeFilter, {NTRP_LEVELS} from "./NtrpRangeFilter";

const LookingPartner = () => {
    const [offers, setOffers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [newOffer, setNewOffer] = useState({
        date: '',
        startTime: '',
        endTime: '',
        addInfo: '',
        level: ''
    });
    const [editOffer, setEditOffer] = useState(null);
    const [userData, setUserData] = useState({});
    const [showForm, setShowForm] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showConfirmButton, setShowConfirmButton] = useState(false);
    const [confirmLabel, setConfirmLabel] = useState("");
    const [modalMessage, setModalMessage] = useState("");
    const [offerToDelete, setOfferToDelete] = useState(null);
    const [showHelp, setShowHelp] = useState(false);
    const [range, setRange] = useState([0, NTRP_LEVELS.length - 1]);
    const [minIdx, maxIdx] = range;

    const {config} = useContext(ConfigContext);

    const isLoggedIn = isTokenValid(getToken());

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (!isLoggedIn) return;
        const fetchData = async () => {
            const data = await getUserData();
            setUserData(data);
        };
        fetchData();
    }, [isLoggedIn]);

    const goToLogin = () => navigate('/login', {state: {from: location.pathname}});

    const handleChange = (event) => {
        const {name, value} = event.target;
        setNewOffer(prev => ({...prev, [name]: value}));
    };

    const fetchAllOffers = async () => {
        setIsLoading(true);
        try {
            const result = await fetchOffers();
            if (result.success) {
                const convertedOffers = result.data.map(offer => ({
                    ...offer,
                    startTime: offer.startTime.substring(0, 5),
                    endTime: offer.endTime.substring(0, 5)
                }));
                convertedOffers.sort((a, b) => new Date(a.date) - new Date(b.date));
                setOffers(convertedOffers);
                setIsLoading(false);
            }
        } catch (error) {
            console.error("Failed to fetch offers", error);
            setError("Błąd pobrania danych");
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAllOffers();
    }, []);

    const validateForm = () => {
        const errors = [];

        if (!userData.showEmail && !userData.showPhone && !userData.showMessenger) {
            errors.push("Ustaw na swoim profilu przynajmniej jeden preferowany kontakt");
        }

        const oneMonthAdvance = new Date();
        oneMonthAdvance.setMonth(oneMonthAdvance.getMonth() + 1);

        const today = new Date();
        today.setHours(0, 0, 0, 0);


        if (new Date(newOffer.date) > oneMonthAdvance) {
            errors.push("Maksymalna data to jeden miesiąc wprzód.");
        }
        if (newOffer.startTime >= newOffer.endTime) {
            errors.push("Czas rozpoczęcia musi być wcześniejszy niż czas zakończenia.");
        }
        if (newOffer.addInfo.length > 200) {
            errors.push("Dodatkowa informacja nie może być dłuższa niż 200 znaków.");
        }
        return errors;
    };


    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsLoading(true);

        const validationErrors = validateForm();

        if (validationErrors.length > 0) {
            setModalMessage(validationErrors.join(" "));
            setIsModalOpen(true);
            setIsLoading(false);
            return;
        }

        try {
            const newOfferWithUser = {...newOffer, userId: userData.id};
            const result = editOffer
                ? await updateOffer(editOffer.id, newOfferWithUser)
                : await addNewOffer(newOfferWithUser);
            if (result.success) {
                setShowForm(false);
                setNewOffer({
                    date: '',
                    startTime: '',
                    endTime: '',
                    addInfo: '',
                    level: ''
                });
                setEditOffer(null);
                fetchAllOffers();
                setModalMessage(result.data);
                setIsModalOpen(true);
            } else {
                setModalMessage(result.data);
                setIsModalOpen(true);
            }
        } catch (error) {
            console.error("Failed to submit new offer", error);
            setError("Error submitting new offer");


        }
        setIsLoading(false);
    };

    const handleContactClick = (contact, type) => {
        if (type === 'email') {
            window.location.href = `mailto:${contact}`;
        } else if (type === 'phone') {
            window.location.href = `tel:${contact}`;
        } else if (type === 'messenger') {
            window.location.href = `https://m.me/${contact}`;
        } else if (type === 'whatsapp') {
            const cleaned = contact.replace(/\D/g, '');
            window.open(`https://wa.me/${cleaned}`, '_blank');
        }
    };


    const generateTimeOptions = () => {
        const times = [];
        for (let hour = 7; hour < 22; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
                const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                times.push(time);
            }
        }
        return times;
    };

    const timeOptions = generateTimeOptions();


    const handleEdit = (offerId) => {
        const offer = offers.find(offer => offer.id === offerId);
        setEditOffer(offer);
        setNewOffer({
            date: offer.date,
            startTime: offer.startTime,
            endTime: offer.endTime,
            addInfo: offer.addInfo,
            level: offer.level
        });
        setShowForm(true);
    };

    const handleDelete = async () => {
        if (offerToDelete) {
            setIsLoading(true);
            try {
                const result = await deleteOffer(offerToDelete);
                if (result.success) {
                    setModalMessage(result.data);
                } else {
                    setModalMessage("Usunięcie nieudane");
                }
            } catch (error) {
                console.error("Failed to delete offer", error);
                setModalMessage("Błąd podczas usuwania oferty.");
            }
            setIsLoading(false);
            setIsModalOpen(true);
            setShowConfirmButton(false);
            setOfferToDelete(null);
            fetchAllOffers();
        }
    };

    const handleDeleteConfirmation = (offerId) => {
        setOfferToDelete(offerId);
        setModalMessage("Czy na pewno chcesz usunąć kartę?");
        setConfirmLabel("Tak, usuwam");
        setShowConfirmButton(true);
        setIsModalOpen(true);
    };

    useEffect(() => {
        if (!showForm) {
            setNewOffer({
                date: '',
                startTime: '',
                endTime: '',
                addInfo: '',
                level: ''
            });
            setEditOffer(null);
        }
    }, [showForm]);

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const isOfferExpired = (date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const offerDate = new Date(date);
        offerDate.setHours(0, 0, 0, 0);

        return offerDate < today;
    };

    const sortedOffers = offers.sort((a, b) => new Date(a.date) - new Date(b.date));
    const visibleByDate = sortedOffers.filter(
        offer => !isOfferExpired(offer.date) || userData.id === offer.userId
    );
    const matchesLevelFilter = (offer) => {
        const lvl = parseFloat(offer.level);
        if (Number.isNaN(lvl)) return true;
        return lvl >= NTRP_LEVELS[minIdx] && lvl <= NTRP_LEVELS[maxIdx];
    };
    const filteredOffers = visibleByDate.filter(matchesLevelFilter);
    const isFilterActive = minIdx !== 0 || maxIdx !== NTRP_LEVELS.length - 1;


    if (isLoading) return <Spinner size={"large"}/>;
    if (error) return <ErrorMessage error={error}/>;


    return (
        <>
            <CustomModal
                isOpen={isModalOpen}
                onRequestClose={handleCloseModal}
                message={modalMessage}
                confirmLabel={showConfirmButton ? confirmLabel : undefined}
                onConfirm={showConfirmButton ? handleDelete : undefined}
                isPositiveMessage={modalMessage.includes("Zapisano") || modalMessage.includes("Skasowane") || modalMessage.includes("Zaktualizowane")}
                cancelLabel="Zamknij"
                confirmButtonColor="red"
                cancelButtonColor="green"
            />
            <div className={styles.container}>
                <div className={styles.titleInfo}>
                    <h5 className={styles.title}>Szukam sparing-partnera</h5>
                </div>

                <div className={styles.helpPanel}>
                    <button
                        type="button"
                        className={styles.helpToggle}
                        onClick={() => setShowHelp(!showHelp)}
                        aria-expanded={showHelp}
                    >
                        {showHelp ? "▾ Ukryj instrukcję" : "▸ Jak to działa? Pokaż instrukcję"}
                    </button>
                    {showHelp && (
                        <div className={styles.helpContent}>
                            <ol className={styles.helpSteps}>
                                <li><b>Wybierz swój poziom NTRP</b> — skala od 1.0 (początkujący) do 5.5 (zaawansowany). Nie wiesz jaki masz poziom? <a href="/ntrp-scale" target="_blank" rel="noreferrer">Sprawdź opis skali NTRP</a>.</li>
                                <li><b>Ustaw datę gry</b> — maksymalnie miesiąc do przodu. Po upłynięciu tej daty Twoja karta automatycznie zniknie z listy.</li>
                                <li><b>Wybierz godziny</b> — przedział w którym jesteś dostępny (co 30 min, 7:00–22:00).</li>
                                <li><b>Dodaj krótki opis</b> — np. preferowane miejsce, doświadczenie, oczekiwania.</li>
                                <li><b>Czekaj na kontakt</b> — inni gracze zobaczą Twoją kartę i skontaktują się przez preferowane kanały (e-mail / telefon / Messenger / WhatsApp) ustawione w Twoim profilu.</li>
                            </ol>

                            <figure className={styles.helpFigure}>
                                <img
                                    src="/partner-offer-example.png"
                                    alt="Przykładowa karta oferty sparing-partnera"
                                    className={styles.helpImage}
                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                />
                                <figcaption>Tak wygląda przykładowa karta oferty na liście.</figcaption>
                            </figure>

                            <p className={styles.helpNote}>
                                Aby dodać własną kartę, musisz mieć w profilu ustawiony przynajmniej jeden preferowany kanał kontaktu (e-mail, telefon, Messenger lub WhatsApp).
                            </p>

                            <div className={styles.helpCallout}>
                                <h6>🔔 Włącz powiadomienia email — nie przegapisz żadnej oferty</h6>
                                <p>
                                    Jeśli <b>szukasz sparing-partnera</b>, nie musisz codziennie sprawdzać aplikacji.
                                    W swoim profilu zaznacz checkbox <b>„Powiadomienia email o nowych sparing-partnerach"</b> i ustaw
                                    interesujący Cię zakres NTRP. Od tego momentu <b>każda nowa oferta
                                    pasująca do Twojego zakresu przyjdzie prosto na Twoją skrzynkę mailową</b> —
                                    bez konieczności logowania i ręcznego sprawdzania.
                                </p>
                                <figure className={styles.helpFigure}>
                                    <img
                                        src="/profile-notifications-checkbox.png"
                                        alt="Checkbox Powiadomienia email o nowych sparing-partnerach w profilu użytkownika"
                                        className={styles.helpImage}
                                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                    />
                                    <figcaption>Profil → „Dane" → zaznacz checkbox i wybierz zakres NTRP.</figcaption>
                                </figure>
                            </div>

                            {!isLoggedIn && (
                                <p className={styles.helpNote}>
                                    <b>Jesteś niezalogowany</b> — przeglądasz oferty w trybie tylko do odczytu. Aby zobaczyć dane kontaktowe lub dodać własną ofertę, zaloguj się.
                                </p>
                            )}
                        </div>
                    )}
                </div>

                <NtrpRangeFilter
                    minIdx={minIdx}
                    maxIdx={maxIdx}
                    onChange={(a, b) => setRange([a, b])}
                    matchCount={filteredOffers.length}
                    totalCount={visibleByDate.length}
                />

                <div className={styles.addOfferContainer}>
                    {isLoggedIn ? (
                        <button className={styles.addOfferButton} onClick={() => setShowForm(!showForm)}>
                            {showForm ? "Zamknij formularz" : "Dodaj nową kartę"}
                        </button>
                    ) : (
                        <button className={styles.addOfferButton} onClick={goToLogin}>
                            Zaloguj się, aby dodać ogłoszenie
                        </button>
                    )}
                </div>

                {showForm && isLoggedIn && (
                    <div className={styles.formContainer}>
                        <form onSubmit={handleSubmit}>
                            <div className={styles.titleRow}>
                                <label htmlFor="level" className={styles.labelOnly}>NTRP</label>
                                <a href="/ntrp-scale" target="_blank" className={styles.infoButton}>Co to jest NTRP?</a>
                            </div>
                            <select
                                id="level"
                                name="level"
                                value={newOffer.level}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Wybierz</option>
                                <option value="1.0">1.0</option>
                                <option value="1.5">1.5</option>
                                <option value="2.0">2.0</option>
                                <option value="2.5">2.5</option>
                                <option value="3.0">3.0</option>
                                <option value="3.5">3.5</option>
                                <option value="4.0">4.0</option>
                                <option value="4.5">4.5</option>
                                <option value="5.0">5.0</option>
                                <option value="5.5">5.5</option>

                            </select>
                            <label htmlFor="date">Data</label>
                            <input
                                type="date"
                                id="date"
                                name="date"
                                value={newOffer.date}
                                onChange={handleChange}
                                required
                            />
                            <label htmlFor="startTime">W godzinach od</label>
                            <select
                                id="startTime"
                                name="startTime"
                                value={newOffer.startTime}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Wybierz</option>
                                {timeOptions.map((time) => (
                                    <option key={time} value={time}>{time}</option>
                                ))}
                            </select>
                            <label htmlFor="endTime">do</label>
                            <select
                                id="endTime"
                                name="endTime"
                                value={newOffer.endTime}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Wybierz</option>
                                {timeOptions.map((time) => (
                                    <option key={time} value={time}>{time}</option>
                                ))}
                            </select>
                            <label htmlFor="addInfo">Dodatkowa informacja</label>
                            <textarea
                                id="addInfo"
                                name="addInfo"
                                value={newOffer.addInfo}
                                onChange={handleChange}
                                placeholder="np. często mogę grać w godzinach przed południem, etc ..."
                            />
                            <button type="submit" className={styles.submitButton}>
                                {editOffer ? "Edytuj" : "Zapisz"}
                            </button>
                        </form>
                    </div>
                )}

                {filteredOffers.length === 0 ? (
                    <p className={styles.addInfo}>
                        {isFilterActive ? "Brak ogłoszeń w wybranym zakresie NTRP." : "Aktualnie brak ogłoszeń."}
                    </p>
                ) : (
                    <div className={styles.offerContainer}>
                        {filteredOffers.map((offer) => (
                            <div key={offer.id}
                                 className={`${styles.offerCard} ${isOfferExpired(offer.date) ? styles.pastOfferCard : ''}`}>
                                <div className={styles.cardHeader}>
                                    <h5>{offer.firstName}</h5>
                                    <span className={styles.boldText}>NTRP {offer.level}</span>
                                </div>
                                <p className={styles.offerDate}>{`${new Date(offer.date).toLocaleDateString('pl-PL', {
                                    weekday: 'long',
                                    day: 'numeric',
                                    month: 'long'
                                })}`}</p>
                                <p className={styles.offerTime}>{`${offer.startTime} - ${offer.endTime}`}</p>
                                <p className={styles.addInfo}>{offer.addInfo}</p>
                                {isLoggedIn ? (
                                    <div className={styles.contactIcons}>
                                        {offer.userShowEmail &&
                                            <FaEnvelope onClick={() => handleContactClick(offer.userEmail, 'email')}/>}
                                        {offer.userShowPhone &&
                                            <FaPhone onClick={() => handleContactClick(offer.userPhone, 'phone')}/>}
                                        {offer.userShowMessenger && <FaFacebookMessenger
                                            onClick={() => handleContactClick(offer.userMessenger, 'messenger')}/>}
                                        {offer.userShowWhatsApp && (
                                            <span title="Po kliknięciu otworzy się czat WhatsApp">
                                             <FaWhatsapp onClick={() => handleContactClick(offer.userWhatsApp, 'whatsapp')} />
                                             </span>
                                        )}
                                    </div>
                                ) : (
                                    <button type="button" className={styles.loginToContact} onClick={goToLogin}>
                                        <FaLock/> Zaloguj się, aby zobaczyć kontakt
                                    </button>
                                )}
                                {userData.id === offer.userId && (
                                    <div className={styles.ownerButtons}>
                                        {config.enableReservation && (
                                            <button onClick={() => handleEdit(offer.id)}>Edytuj</button>
                                        )}
                                        <button onClick={() => handleDeleteConfirmation(offer.id)}>Usuń</button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
};

export default LookingPartner;

